/**
 * Best-effort screenshot of the visible page for feedback notes.
 *
 * html2canvas is lazy-imported so it costs nothing in the main bundle until a
 * reporter actually opens the composer. It cannot read Cornerstone's WebGL
 * viewport canvas — that renders blank, which is acceptable here: UI/wording
 * feedback is about the DOM chrome, and the exact study/slice is still
 * recoverable from the captured context (study_uid + sliceIndex). The blank
 * canvas is NOT a PHI guarantee, though — DOM text (report editor, worklist)
 * can carry PHI, which is why the backend stores these screenshots in a
 * private, SSE'd, lifecycle-expiring bucket.
 */

import { logger } from "@/utils/logger";

const SCALE = 0.6;
const JPEG_QUALITY = 0.8;

/** Capture the current viewport as a JPEG blob, or null on any failure. */
export async function captureScreenshot(): Promise<Blob | null> {
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(document.body, {
      backgroundColor: "#0A0A0B",
      scale: SCALE,
      logging: false,
      useCORS: true,
      // Visible viewport only — not the full scrollable height.
      width: window.innerWidth,
      height: window.innerHeight,
      x: window.scrollX,
      y: window.scrollY,
    });
    return await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => resolve(blob), "image/jpeg", JPEG_QUALITY);
    });
  } catch (err) {
    logger.warn(`[feedback] screenshot capture failed: ${String(err)}`);
    return null;
  }
}
